import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { filter, Subject, takeUntil } from 'rxjs';

import { DialogNotification, FeatureFlagsConfigService, NotificationDialogComponent } from '@olympus/ngx-shared';
import { CryptlexLicense, EpochTimestampInSeconds, FlattenedLicenseFeature, getLicenseEnabledFeatures, LicenseStatus } from 'electron-app/licenses';
import { DialogService } from 'shared-modules-angular/dialog';
import { ToastService } from 'shared-modules-angular/toast';

import { CryptlexLicenseService } from '../services/cryptlex-license.service';

export interface PcLicenseModalDialogNotification extends DialogNotification {
  licenseStatus?: LicenseStatus;
  promptRestartOnLicenseChange: boolean;
}

@Component({
  selector: 'app-pc-license-modal',
  styleUrls: ['./pc-license.component.scss'],
  templateUrl: './pc-license.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PcLicenseModalComponent implements OnDestroy, OnInit {
  public static readonly WIDTH = '600px';
  public static readonly HEIGHT = '600px';

  private readonly _unsubscribe: Subject<void> = new Subject();

  public licenseKeyModel = '';
  public showErrorMessage = false;
  public errorMessage: string;
  public license: CryptlexLicense;

  public licenseReadOnly = false;
  public enabledFeatures: FlattenedLicenseFeature[] = [];

  private _showDeactivationWarning = false;

  public get showDeactivationWarning(): boolean {
    return this._showDeactivationWarning;
  }

  public set showDeactivationWarning(value: boolean) {
    this._showDeactivationWarning = value;
    this._changeDetectorRef.detectChanges();
  }

  public get isLicenseActivated(): boolean {
    return this.license?.status === 'activated' || this._pcLicenseService.isInGracePeriod;
  }

  public get canDeactivate(): boolean {
    return !!this.license?.key;
  }

  public get appName(): string {
    return this._featureFlagsConfigService.appConfig.appName;
  }

  constructor(
    @Inject(DIALOG_DATA) public notification: PcLicenseModalDialogNotification,
    public dialogRef: DialogRef,
    private readonly _pcLicenseService: CryptlexLicenseService,
    private readonly _toastService: ToastService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _dialogService: DialogService,
    private readonly _featureFlagsConfigService: FeatureFlagsConfigService,
  ) {
  }

  public ngOnInit(): void {
    this.handleLicenseActivation();
    this.handleLicense();
    this.handleLicenseDeactivation();
    this.handleLicenseNumberOfDaysBeforeExpiration();
  }

  public closeApp(): void {
    window.electron.send('closeApp');
  }

  public closeDialog(): void {
    this.dialogRef.close(this.notification);
  }

  public toggleDeactivateButtonDisplay(): void {
    this.showDeactivationWarning = !this.showDeactivationWarning;
    this.showErrorMessage = false;
  }

  public activateLicense(): void {
    this._pcLicenseService.activateLicense(this.licenseKeyModel);
    this.errorMessage = '';
    this.showErrorMessage = false;
    this._changeDetectorRef.detectChanges();
  }

  public deactivateLicense(): void {
    this._pcLicenseService.deactivateLicense();
    this.errorMessage = '';
    this.showErrorMessage = false;
    this._changeDetectorRef.detectChanges();
  }

  public getExpirationDate(timestamp: EpochTimestampInSeconds | false): string {
    if (timestamp === null || timestamp === undefined) {
      return 'N/A';
    }

    if (timestamp === 0 || timestamp === false) {
      return 'perpetual';
    }

    return DateTime.fromSeconds(timestamp).toISODate();
  }

  public isExpired(date: number): boolean {
    if (!date) {
      return false;
    }

    return new Date(date * 1000) < new Date();
  }

  public ngOnDestroy(): void {
    this._unsubscribe.next();
  }

  private getLicense(): void {
    this._pcLicenseService.getLicense();
  }

  private handleLicenseActivation(): void {
    this._pcLicenseService.handleLicenseActivation$.pipe(
      takeUntil(this._unsubscribe),
    ).subscribe((status) => {
      this.notification.licenseStatus = status;

      switch (status) {
        case 'activated':
          this._toastService.success({ description: 'License activated' });

          this.dialogRef.close(this.notification);

          if (this.notification.promptRestartOnLicenseChange) {
            this._dialogService.open(NotificationDialogComponent, {
              data: {
                headerTitle: 'Application must be restarted',
                titleIconName: 'exclamation-triangle',
                bodyMessage: 'Since the license has changed, in order to use all the features, please restart the application',
              },
              height: NotificationDialogComponent.DEFAULT_HEIGHT,
              width: NotificationDialogComponent.DEFAULT_WIDTH,
              disableClose: true,
            });
          }

          break;

        case 'expired':
          this.errorMessage = 'This license has expired. Please contact your Evident sales rep.';
          this.showErrorMessage = true;
          break;

        case 'suspended':
          this.errorMessage = 'This license has been suspended. Please contact your Evident sales rep.';
          this.showErrorMessage = true;
          break;

        case 'grace-period-over':
          this.errorMessage = 'The grace period is over. Please contact your Evident sales rep to obtain a valid license key.';
          this.showErrorMessage = true;
          break;

        case 'failed':
        default:
          this.errorMessage = 'License activation failed. Please try again using a different license key, or contact your Evident sales rep.';
          this.showErrorMessage = true;
          break;
      }

      this.getLicense();
      this._changeDetectorRef.detectChanges();
    });
  }

  private handleLicense(): void {
    this._pcLicenseService.handleLicense$.pipe(
      filter(license => license.status !== 'failed'),
      takeUntil(this._unsubscribe),
    ).subscribe((license) => {
      this.license = license;
      this.licenseKeyModel = license.key;
      this.notification.licenseStatus = license.status;
      this.licenseReadOnly = typeof license.key === 'string' && license.key.trim().length > 0;
      this.enabledFeatures = getLicenseEnabledFeatures(license.features);
      this._changeDetectorRef.detectChanges();
    });
  }

  private handleLicenseDeactivation(): void {
    this._pcLicenseService.handleLicenseDeactivation$.pipe(
      takeUntil(this._unsubscribe),
    ).subscribe((status) => {
      switch (status) {
        case 'deactivated':
          this.closeApp();
          break;
        case 'network-error':
          this.errorMessage = 'License deactivation failed. Please check your internet connection and try again.';
          this.showErrorMessage = true;
          break;
        case 'failed':
        default:
          this.errorMessage = 'License deactivation failed. Please try again, or contact your Evident sales rep.';
          this.showErrorMessage = true;
          break;
      }

      this._changeDetectorRef.detectChanges();
    });
  }

  private handleLicenseNumberOfDaysBeforeExpiration(): void {
    this._pcLicenseService.handleLicenseStatusIndicator$.pipe(
      filter(licenseDaysStatus => licenseDaysStatus.status === 'warning'),
      takeUntil(this._unsubscribe),
    ).subscribe((licenseDaysStatus) => {
      this.errorMessage = `This license is expiring soon (${licenseDaysStatus.days} days left). Please contact your Evident sales rep. `;
      this.showErrorMessage = true;

      this._changeDetectorRef.detectChanges();
    });
  }
}
